import React, { useRef, useEffect } from "react";
import { CheckCircle } from "lucide-react";
import { apiService } from "../services/api";
import useApi from "../hooks/useApi";
import { ErrorMessage } from "./ErrorBoundary";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const {
    data: companyStats,
    loading,
    error,
    refetch,
  } = useApi(() => apiService.getCompanyStats(), []);

  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;

    if (!video || !container) return;

    video.play().catch(() => {});

    const ctx = gsap.context(() => {
      gsap.fromTo(
        video,
        { scale: 0.9 },
        {
          scale: 1.05,
          ease: "power2.out",
          scrollTrigger: {
            trigger: container,
            start: "top 80%",
            end: "bottom 20%",
            scrub: true,
          },
        }
      );
    }, container);

    return () => ctx.revert();
  }, []);

  if (error) {
    return (
      <section id="about" className="section-padding bg-white">
        <div className="container mx-auto px-6 lg:px-8">
          <ErrorMessage message={error} onRetry={refetch} />
        </div>
      </section>
    );
  }

  return (
    <section id="about" className="section-padding bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* LEFT CONTENT */}
          <div className="w-full lg:w-1/2 animate-fadeInLeft">
            <div className="flex items-center space-x-2 mb-6">
              <div className="h-1 w-12 bg-[#2aa996]" />
              <span className="text-[#2aa996] font-semibold uppercase tracking-wider text-sm">
                About Transpost
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Building clarity in a changing world
              {/* <span className="gradient-text"> We Help You Get There.</span> */}
            </h2>

            <p className="text-base sm:text-lg text-gray-600 mb-6 leading-relaxed">
              Transpost helps organizations move forward with clarity and
              confidence. We look beyond today to understand how technology is
              evolving, how industries are shifting, and what that means for the
              people and systems that power them.
            </p>

            <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed">
              Our role is to turn long term understanding into practical
              strategies, systems, and products. We work closely with
              organizations to reduce complexity, make better decisions, and
              build capabilities that remain meaningful over time.
            </p>
            <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed">
              With experience delivering complex systems across industries, we
              bring perspective, care, and long range thinking to every
              engagement. Our teams work alongside yours to focus on what truly
              matters, design solutions that fit your reality, and support
              steady, sustainable progress.
            </p>

            <div className="space-y-4 mb-8">
              {[
                {
                  title: "Vision-First Transformation",
                  desc: "We help organizations understand where their industry is heading and shape a clear path forward. Every solution is guided by long term thinking, not short term trends.",
                },
                {
                  title: "Strategy & Development",
                  desc: " From identifying where intelligence truly adds value to designing and building custom systems, we help organizations improve how they operate, serve customers, and scale with confidence.",
                },
                {
                  title: "Software Built for Tomorrow",
                  desc: " We design and develop enterprise software and industry specific products that are adaptable, resilient, and ready to evolve as technology and needs change.",
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start space-x-4">
                  <CheckCircle
                    className="text-[#2aa996] mt-1 flex-shrink-0"
                    size={20}
                  />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">
                      {item.title}
                    </h4>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-[#2aa996]">
              <h4 className="font-bold text-gray-800 mb-2">Our Vision</h4>
              <p className="text-gray-600 italic">
                "We work with leaders who want to move forward with intention,
                not urgency. By combining thoughtful strategy with well designed
                systems, we help organizations navigate change, reduce
                uncertainty, and build what lasts. Technology is not the goal.
                Clarity, confidence, and meaningful progress are."
              </p>
            </div>
          </div>

          {/* RIGHT CONTENT */}
          <div className="w-full lg:w-1/2 flex flex-col items-center animate-fadeInRight">
            {/* Reel Video */}
            <div
              ref={containerRef}
              className="w-full max-w-sm aspect-[9/16] overflow-hidden rounded-3xl  mb-10"
            >
              <video
                ref={videoRef}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="w-full h-full object-cover"
                src="https://www.pexels.com/download/video/18069233/"
              />
            </div>

            {/* Core Values */}
            <div className="bg-gradient-to-br from-[#2aa996] to-[#1e7a6b] text-white rounded-2xl p-8 w-full max-w-md">
              <h3 className="text-2xl font-bold mb-6">Our Core Values</h3>

              <div className="space-y-4">
                {[
                  "Innovation & Excellence",
                  "Security & Reliability",
                  "Client Success Focus",
                  "Transparent Communication",
                ].map((value) => (
                  <div key={value} className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-white rounded-full" />
                    <span className="font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
